/**
 * `AW14` · La fuente de la audiencia elegible de la pantalla «Envíos» (`F2`).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ① LO QUE ENTRA DEL REPO SIGUEN SIENDO SÓLO TIPOS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Los imports de `contracts/**` son `import type`. La ruta se escribe RELATIVA
 * (`../../../../contracts/read-api`) y no con el alias `@contracts/…`: el alias
 * vive en `tsconfig.json` y **NO** en `vite.config.ts`, así que un import que
 * dependiera de él rompe el build del navegador. `R2-UI2` (`AW56`) fija esta
 * forma; `borde-envios.ts` lleva el mismo acuerdo y el mismo argumento.
 *
 * ⚠️ **`contracts/read-api` es un puerto de TypeScript y no tiene `http.ts`**, así
 * que `GET elegibles` es una **invención de `apps/admin-web`** y el segmento de
 * ruta va como literal sin tipo. No lo protege nada; se dice acá.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ② LO QUE SÍ QUEDA ANCLADO CONTRA EL CONTRATO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * · **Los nombres de TODOS los parámetros de la consulta**, contra
 *   `keyof EligibleQuery`: `page`, `pageSize`, `lotes` y `estados`. Si el
 *   contrato renombra cualquiera de los cuatro, esto no compila.
 * · **La forma de la respuesta**: la proyección se escribe campo por campo desde
 *   un valor validado en runtime, así que un renombre se cae acá.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ③ ⛔⭐ NI UNA COLUMNA DE PERSONA DE MÁS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Del otro lado del cable hay nombre y teléfono de menores. `leerFila` construye
 * un objeto literal con ~~exactamente tres campos~~ exactamente los siete de
 * `EligibleListItem`, así que una fila que llegara con uno más —un `rut`, un
 * `email`, un `optOut`, una `…FechaHora`— no entra a la memoria de la pantalla.
 *
 * ⭐ RASTRO · 24/9/2026 · `R2-UI13` (`PRD_R2.md:1402`, `CA-23`/`CA-27`). Eran tres
 * (`id`, `name`, `phone`) y la consola tiraba `lote` y los tres flags que el cable
 * YA traía; la Tabla 1 los necesita. Siguen saliendo de ESTA lectura: no se cruza
 * contra la página del padrón, que trae los campos que esta vista deja fuera.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ④ ACÁ NO SE FILTRA NI SE PAGINA — Y EL OR ES DEL SERVIDOR (`CA-30`)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `page` y `pageSize` se PIDEN y `total` se usa TAL CUAL: es el total en la BASE
 * del conjunto consultado. **`lotes` y `estados` se mandan REPETIDOS** (un
 * `?estados=` por cada estado) y el OR entre ellos lo resuelve el servidor: la
 * pantalla NO filtra en el cliente. Rehacer el OR acá bajaría el padrón entero
 * para descartarlo en el navegador, que es justo lo que `ADR-0090` descartó.
 *
 * **El brazo no viaja**: no hay `?arm=`. Lo resuelve `admin-web` de la sesión
 * (`ADR-0009` condición 5). Un brazo elegible desde el cliente es la primera
 * petición manipulada que baja el padrón del otro brazo.
 */

import type { EligibleQuery } from "../../../../contracts/read-api/index.ts"
import type { ConfiguracionInstancia } from "../configuracion.ts"
import { mensajeDeError } from "../mensaje-de-error.ts"
import type {
  CargarElegibles,
  ConsultaDeAudiencia,
  FilaDeAudiencia,
  PaginaDeAudiencia,
} from "./formulario.tsx"
import { raizDelBrazo } from "../brazo-del-cable.ts"

/**
 * El segmento de ruta, **sin ancla de tipo y a sabiendas** (ver ① arriba).
 *
 * `apps/admin-web/src/rutas.ts` lo declara con este mismo valor. Es el único
 * acuerdo de cable de este archivo que ningún tipo hace cumplir: si `admin-web`
 * lo renombra, esto compila igual y falla en runtime con un `404`.
 */
const RUTA_ELEGIBLES = "elegibles"

/**
 * Los nombres de los parámetros, anclados contra los campos del contrato.
 *
 * No es cosmético: son los únicos strings de este archivo que `tsc` puede
 * defender, y defienden justo el caso silencioso — un `pageSize` renombrado no
 * daría `404`, daría un `400` que en pantalla se lee como «no se pudo leer la
 * audiencia».
 */
const PARAMETRO_PAGINA: keyof EligibleQuery = "page"
const PARAMETRO_TAMANO: keyof EligibleQuery = "pageSize"
const PARAMETRO_LOTES: keyof EligibleQuery = "lotes"
const PARAMETRO_ESTADOS: keyof EligibleQuery = "estados"

/** Error del borde con la forma que el resto de la app ya usa (`HttpError`). */
function errorDelBorde(mensaje: string, statusCode: number): Error & { statusCode: number } {
  return Object.assign(new Error(mensaje), { statusCode })
}

function esObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === "object" && valor !== null && !Array.isArray(valor)
}

/**
 * Valida una fila del cable y la PROYECTA a ~~los tres~~ los siete campos de la
 * pantalla (`R2-UI13`).
 *
 * La traducción de nombres (`id`→`idUsuario`, `name`→`nombre`, `phone`→`telefono`)
 * es de esta capa, tal como `rutas.ts` la deja asignada. `lote` y los tres flags
 * conservan el nombre del contrato.
 *
 * ⛔ `R2-UI13` · los cuatro campos nuevos se validan con la MISMA regla que los
 * tres de antes: una fila que no los trae con su tipo (texto y tres `boolean`)
 * invalida la PÁGINA. No se completa con un valor inventado: un flag ausente
 * dibujado como «No» sería una afirmación sobre un participante que nadie hizo.
 */
function leerFila(valor: unknown): FilaDeAudiencia | null {
  if (!esObjeto(valor)) return null

  const id = valor["id"]
  const name = valor["name"]
  const phone = valor["phone"]
  if (typeof id !== "string" || typeof name !== "string" || typeof phone !== "string") return null

  const lote = valor["lote"]
  const sinInteraccion = valor["sinInteraccion"]
  const siguioConChatbot = valor["siguioConChatbot"]
  const pidioHumano = valor["pidioHumano"]
  if (
    typeof lote !== "string" ||
    typeof sinInteraccion !== "boolean" ||
    typeof siguioConChatbot !== "boolean" ||
    typeof pidioHumano !== "boolean"
  ) {
    return null
  }

  return {
    idUsuario: id,
    nombre: name,
    telefono: phone,
    lote,
    sinInteraccion,
    siguioConChatbot,
    pidioHumano,
  }
}

/** Valida la página: las filas más el total EN LA BASE del conjunto consultado. */
function leerPagina(valor: unknown): PaginaDeAudiencia | null {
  if (!esObjeto(valor)) return null

  const itemsCrudo = valor["items"]
  if (!Array.isArray(itemsCrudo)) return null

  const filas: FilaDeAudiencia[] = []
  for (const item of itemsCrudo) {
    const fila = leerFila(item)
    if (fila === null) return null
    filas.push(fila)
  }

  const total = valor["total"]
  if (typeof total !== "number" || !Number.isInteger(total) || total < 0) return null

  return { filas, total }
}

/**
 * Arma la fuente de audiencia contra la `admin-web` configurada.
 *
 * Función y no constante por lo mismo que los otros bordes: la dirección llega en
 * tiempo de ejecución (`configuracion.json`).
 *
 * ⚠️ **Lo que devuelve tiene que ser ESTABLE entre renders.** `CargarElegibles`
 * es una dependencia del `useEffect` que carga la tabla en `formulario.tsx`, así
 * que `App.tsx` la construye con `useMemo` sobre `config`.
 */
export function crearCargarElegiblesHttp(config: ConfiguracionInstancia): CargarElegibles {
  /** ⭐ `AR5-m` · ⟦el-brazo-entra-por-ruta⟧ · la raíz lleva el brazo adelante. */
  const raiz = (): string => raizDelBrazo(config.adminWebUrl)

  return async (consulta: ConsultaDeAudiencia): Promise<PaginaDeAudiencia> => {
    const parametros = new URLSearchParams()
    parametros.set(PARAMETRO_PAGINA, String(consulta.pagina))
    parametros.set(PARAMETRO_TAMANO, String(consulta.tamanoDePagina))

    // ⛔ CA-30: `lotes` y `estados` van REPETIDOS y el OR lo hace el servidor.
    for (const lote of consulta.lotes) parametros.append(PARAMETRO_LOTES, lote)
    for (const estado of consulta.estados) parametros.append(PARAMETRO_ESTADOS, estado)

    /**
     * `credentials: "include"` y ningún `Authorization` armado acá: la sesión es
     * una cookie `HttpOnly` que el navegador manda sola.
     */
    let respuesta: Response
    try {
      respuesta = await fetch(`${raiz()}/${RUTA_ELEGIBLES}?${parametros.toString()}`, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
      })
    } catch (causa) {
      throw Object.assign(errorDelBorde(`No se pudo hablar con el servidor (${config.adminWebUrl}).`, 0), {
        cause: causa,
      })
    }

    if (!respuesta.ok) {
      throw errorDelBorde(
        await mensajeDeError(respuesta, `El servidor respondió ${respuesta.status}.`),
        respuesta.status,
      )
    }

    const pagina = leerPagina(await respuesta.json())
    if (pagina === null) {
      throw errorDelBorde(
        "El servidor contestó la audiencia elegible en una forma que no se entiende.",
        502,
      )
    }
    return pagina
  }
}
